/**
 * Extra-rider pricing for per-vehicle packages — single source of truth for
 * every surface (api, web, admin, mobile).
 *
 * Per-vehicle pricing (`typePrice 3`) charges a flat rate per quad / buggy /
 * jet ski / boat. Two numbers describe how many people that rate covers:
 *
 * - `maxGuests` — how many people physically fit on ONE unit (a quad seats 2).
 * - `includedGuests` — how many the flat rate actually pays for. A 300cc quad
 *   is priced for a single rider (`includedGuests = 1`) even though a second
 *   person can sit behind; a 700cc quad is priced for two (`includedGuests = 2`).
 *
 * When `includedGuests` is null the flat rate covers the whole unit, so it
 * falls back to `maxGuests` and no surcharge can ever apply. That fallback is
 * what keeps every pre-existing per-vehicle package behaving exactly as before.
 *
 * Riders beyond the included count are charged `extraGuestPrice` each. The
 * Dakhla 300cc quad, for instance, is 400 MAD for one rider and +100 MAD for a
 * second sharing the same machine — 500 MAD total, not 400 and not 800.
 *
 * Sharing is only offered when BOTH `includedGuests < maxGuests` (there is a
 * free seat) AND `extraGuestPrice > 0` (the operator published a price for it).
 * Without a published price the guest picker stays capped at the included
 * count, so a second rider can never slip through unpriced.
 */

/** Negative/undefined price = not offered; clamp to 0. */
function clampPrice(p: number | null | undefined): number {
  return typeof p === "number" && p > 0 ? p : 0;
}

function positiveInt(n: number | null | undefined): number | null {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/** Per-vehicle shape the helpers below read. Every field is optional so older
 *  callers that never learned about extra riders keep compiling and keep their
 *  current (surcharge-free) behaviour. */
export interface ExtraGuestPackage {
  /** People that physically fit on one unit. `null` = uncapped. */
  maxGuests?: number | null;
  /** People the flat rate pays for. `null` = the whole unit. */
  includedGuests?: number | null;
  /** Price per rider beyond `includedGuests`. `null`/0 = sharing not sold. */
  extraGuestPrice?: number | null;
}

/**
 * How many riders one unit's flat rate covers. Falls back to `maxGuests`, then
 * to 1, so a package that declares neither still prices a lone rider sanely.
 * Never exceeds `maxGuests` — an operator typo cannot make the included count
 * larger than the machine.
 */
export function includedGuestsPerUnit(pkg: ExtraGuestPackage): number {
  const capacity = positiveInt(pkg.maxGuests);
  const included = positiveInt(pkg.includedGuests);
  if (included == null) return capacity ?? 1;
  return capacity == null ? included : Math.min(included, capacity);
}

/** True when this package sells a seat beyond the included count. */
export function allowsExtraGuests(pkg: ExtraGuestPackage): boolean {
  if (clampPrice(pkg.extraGuestPrice) <= 0) return false;
  const capacity = positiveInt(pkg.maxGuests);
  // Uncapped capacity with a published price = sharing allowed without ceiling.
  if (capacity == null) return true;
  return includedGuestsPerUnit(pkg) < capacity;
}

/**
 * Headcount ceiling for `units` vehicles.
 *
 * Sharing packages cap at physical capacity (`maxGuests × units`); packages
 * with no published extra-rider price cap at what the fare covers
 * (`includedGuests × units`), which is what stops a second rider riding free.
 *
 * Returns `null` when the package declares no usable cap — callers treat that
 * as "uncapped" and fall back to their own ceiling.
 */
export function extraGuestCapacity(pkg: ExtraGuestPackage, units: number): number | null {
  // Neither number declared = the package never had a ceiling; keep it that way
  // rather than inventing the one-rider fallback `includedGuestsPerUnit` uses
  // for pricing.
  if (positiveInt(pkg.maxGuests) == null && positiveInt(pkg.includedGuests) == null) return null;
  const perUnit = allowsExtraGuests(pkg) ? positiveInt(pkg.maxGuests) : includedGuestsPerUnit(pkg);
  if (perUnit == null) return null;
  return perUnit * Math.max(1, Math.floor(units) || 1);
}

/** Riders beyond what the flat rate covers across `units` vehicles. */
export function extraGuestCount(pkg: ExtraGuestPackage, guests: number, units: number): number {
  if (!allowsExtraGuests(pkg)) return 0;
  const covered = includedGuestsPerUnit(pkg) * Math.max(1, Math.floor(units) || 1);
  const heads = Math.max(0, Math.floor(guests) || 0);
  return Math.max(0, heads - covered);
}

/**
 * Surcharge owed for riders sharing a unit. `0` whenever the package does not
 * sell sharing, so a stale guest count can never add a phantom charge.
 */
export function resolveExtraGuestPrice(
  pkg: ExtraGuestPackage,
  guests: number,
  units: number,
): number {
  return extraGuestCount(pkg, guests, units) * clampPrice(pkg.extraGuestPrice);
}
